import { onUnmounted } from "vue";
import { useStore } from "@/core/services/store";
import ApiService from "@/core/services/api.service";
import { SET_INSTANCE } from "@/core/services/store/instance.module";

const EXCHANGE_TOKEN_ACTION = "exchangeToken";
const EXCHANGE_TOKEN_FAIL_ACTION = "exchangeTokenFail";
const EXCHANGE_TOKEN_GET_ACCESS_TOKEN_FAIL_ACTION = "getAccessTokenFail";
const EXCHANGE_TOKEN_FAIL_ACTIONS = [
  EXCHANGE_TOKEN_FAIL_ACTION,
  EXCHANGE_TOKEN_GET_ACCESS_TOKEN_FAIL_ACTION
];
// How long the host may take to answer the login's `exchangeToken` before the login counts as
// failed; without it a host that never answers leaves the loader spinning.
const EXCHANGE_TOKEN_TIMEOUT_MS = 10 * 1000;

// Re-request the host access token this long before its `exp`. The backend rejects expired
// Xentral tokens with 60 s leeway (CONNECT-7688), so the fresh token lands in time.
const REFRESH_LEAD_MS = 60 * 1000;
// Floor so a host handing back a token already inside the lead window is not asked in a loop.
const MIN_REFRESH_DELAY_MS = 10 * 1000;
// Retry delay after a refresh that failed or got no answer, or whose session renewal failed.
// Also the minimum gap between renewals a 401 may trigger.
const RETRY_DELAY_MS = 30 * 1000;
// setTimeout stores its delay as int32: anything larger overflows and fires immediately.
const MAX_TIMEOUT_MS = 2147483647;

// Page-lifetime refresh state (CONNECT-7724): the Auth view running the handshake unmounts right
// after login, so the refresh must not hang off its lifecycle.
let refreshTimer = null;
let refreshAt = 0;
let awaitingRefresh = false;
let onRefreshedToken = null;
let lastHostToken = null;
let lastRenewalAt = 0;
let renewalFailed = false;

function decodeExpiry(token) {
  try {
    const payload = JSON.parse(
      window.atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

function stopTimer() {
  clearTimeout(refreshTimer);
  refreshTimer = null;
}

function startTimer(delay) {
  clearTimeout(refreshTimer);
  refreshAt = Date.now() + delay;
  refreshTimer = setTimeout(requestHostToken, delay);
}

// The retry is armed up front: a usable reply replaces it with the next regular refresh, so a
// failed or unanswered request is simply asked again.
function requestHostToken() {
  // Still waiting means the previous request got no answer.
  if (awaitingRefresh) renewalFailed = true;
  awaitingRefresh = true;
  startTimer(RETRY_DELAY_MS);
  window.top?.postMessage({ action: EXCHANGE_TOKEN_ACTION }, "*");
}

function scheduleRefresh(accessToken) {
  const exp = decodeExpiry(accessToken);
  if (exp === null) {
    stopTimer();
    return;
  }
  startTimer(
    Math.min(
      Math.max(exp * 1000 - REFRESH_LEAD_MS - Date.now(), MIN_REFRESH_DELAY_MS),
      MAX_TIMEOUT_MS
    )
  );
}

function renewSession(token) {
  lastHostToken = token;
  lastRenewalAt = Date.now();
  Promise.resolve()
    .then(() => onRefreshedToken(token))
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error("Renewing the session failed", error);
      // The refresh was given up meanwhile (logout): nothing to retry.
      if (!onRefreshedToken) return;
      renewalFailed = true;
      // Forget the token so the retry renews even if the host hands back the same one.
      lastHostToken = null;
      startTimer(RETRY_DELAY_MS);
    });
}

// Timers stall while the device sleeps, so the wall clock is checked again when the user returns.
function refreshIfOverdue() {
  if (refreshTimer !== null && Date.now() >= refreshAt) requestHostToken();
}

function handleVisibilityChange() {
  if (document.visibilityState === "visible") refreshIfOverdue();
}

// Only answers to our own refresh request count; the initial exchange is handled by the view.
// The request went to window.top, so no other frame may answer it with a token of its own.
function handleRefreshReply(event) {
  if (!awaitingRefresh || event.source !== window.top) return;
  const action = event.data?.action?.toString();
  const token = event.data?.data;
  if (action === EXCHANGE_TOKEN_ACTION && typeof token === "string" && token) {
    awaitingRefresh = false;
    renewalFailed = false;
    // A host handing back the token it already gave out needs no new session.
    if (token !== lastHostToken) renewSession(token);
    scheduleRefresh(token);
  } else if (
    action === EXCHANGE_TOKEN_ACTION ||
    EXCHANGE_TOKEN_FAIL_ACTIONS.includes(action)
  ) {
    // The retry armed by requestHostToken asks again.
    awaitingRefresh = false;
    renewalFailed = true;
    // eslint-disable-next-line no-console
    console.error("Refreshing the host access token failed");
  }
}

function startRefresh(accessToken, callback) {
  onRefreshedToken = callback;
  lastHostToken = accessToken;
  lastRenewalAt = Date.now();
  renewalFailed = false;
  // addEventListener ignores an already registered listener, so remounts don't stack them.
  window.addEventListener("message", handleRefreshReply);
  window.addEventListener("focus", refreshIfOverdue);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  scheduleRefresh(accessToken);
}

// Renews the session in place after the backend rejected it with a 401, e.g. once the device
// slept past the Connect JWT's expiry. Returns false when the session cannot be renewed: the page
// has no refresh, or the last attempt failed. The caller logs out then, which ends the refresh.
export function renewSessionNow() {
  if (!onRefreshedToken) return false;
  if (renewalFailed) {
    stopTimer();
    awaitingRefresh = false;
    onRefreshedToken = null;
    return false;
  }
  // At most one renewal per retry interval, so a burst of 401s (or an endpoint that keeps
  // answering 401) does not turn into a /apps/login per request.
  if (!awaitingRefresh && Date.now() - lastRenewalAt >= RETRY_DELAY_MS) {
    // The host may hand back the token the rejected session came from; it is traded again.
    lastHostToken = null;
    requestHostToken();
  }
  return true;
}

// Logs in with the access token the host exchanges for this app; the host's id token is never
// used to log in. Passing `onTokenRefreshed` opts in to the page-lifetime host token refresh. It
// receives each refreshed access token; when it returns a promise that rejects, the renewal is
// retried. `onLoginFailed` is called when the exchange fails, returns no token or gets no answer.
export const useAuthentication = (
  loginCallback,
  { onTokenRefreshed, onLoginFailed } = {}
) => {
  const store = useStore();
  // Set while the login's exchange request awaits its answer; other answers are ignored.
  let exchangeTimeout = null;

  function settleExchange() {
    if (exchangeTimeout === null) return false;
    clearTimeout(exchangeTimeout);
    exchangeTimeout = null;
    window.removeEventListener("message", handleTokenMessage);
    return true;
  }

  function failLogin(message) {
    // eslint-disable-next-line no-console
    console.error(message);
    onLoginFailed?.();
  }

  function handleTokenMessage(event) {
    // Replies to a page-level refresh belong to handleRefreshReply.
    if (awaitingRefresh || event.source !== window.top) return;

    const action = event.data?.action?.toString();
    const isFail = EXCHANGE_TOKEN_FAIL_ACTIONS.includes(action);
    if (action !== EXCHANGE_TOKEN_ACTION && !isFail) return;
    if (!settleExchange()) return;

    const accessToken = event.data?.data;
    if (isFail) {
      failLogin("Token exchange failed");
      return;
    }
    if (typeof accessToken !== "string" || !accessToken) {
      failLogin("Token exchange returned no token");
      return;
    }

    if (onTokenRefreshed) startRefresh(accessToken, onTokenRefreshed);
    loginCallback(accessToken);
  }

  // Keeps the instance (incl. xentralVersion) in the store for version-dependent features; the
  // login does not wait for it.
  async function loadInstance() {
    try {
      const response = await ApiService.get(
        process.env.VUE_APP_API_ADMIN + "/instance"
      );
      store.dispatch("instance/" + SET_INSTANCE, response.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Loading the instance failed", error);
    }
  }

  // Called once the host config (and with it the license id) is in the store.
  function requestAccessToken() {
    loadInstance();
    settleExchange();
    window.addEventListener("message", handleTokenMessage);
    // A fresh handshake owns the next reply, even if a page-level refresh never got one.
    awaitingRefresh = false;
    exchangeTimeout = setTimeout(() => {
      exchangeTimeout = null;
      window.removeEventListener("message", handleTokenMessage);
      failLogin("Token exchange timed out");
    }, EXCHANGE_TOKEN_TIMEOUT_MS);
    window.top?.postMessage({ action: EXCHANGE_TOKEN_ACTION }, "*");
  }

  onUnmounted(() => {
    settleExchange();
  });

  return { requestAccessToken };
};
